import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Btn } from '../kit';

/**
 * Form atoms and the e-mail preview shared by the two template tabs
 * ("Templates (editable)" and "Send to users"). EmailPanel keeps its own
 * local atoms; these live beside the two tabs that need a preview, a confirm
 * dialog and a checkbox multi-select, so neither tab reaches into the other.
 */

export type Note = { kind: 'error' | 'ok'; text: string } | null;
export type Cta = { label: string; url: string };

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------
export const Labeled: React.FC<{ label: string; hint?: string; span?: string; children: React.ReactNode }> =
  ({ label, hint, span, children }) => (
  <label className={`flex flex-col gap-1 text-xs text-grayscale-1100 min-w-0 ${span || ''}`}>
    <span>{label}{hint && <span className="text-grayscale-900"> · {hint}</span>}</span>
    {children}
  </label>
);

export const inputCls =
  'h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300 w-full';

export const DateInput: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => (
  <input type="date" value={value} onChange={e => onChange(e.target.value)}
    className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300" />
);

/** Two dates, "from" and "to", either one optional. */
export const DateRange: React.FC<{
  label: string; from: string; to: string; onChange: (from: string, to: string) => void;
}> = ({ label, from, to, onChange }) => (
  <div className="flex flex-col gap-1 text-xs text-grayscale-1100">
    <span>{label}</span>
    <div className="flex items-center gap-1.5 flex-wrap">
      <DateInput value={from} onChange={v => onChange(v, to)} />
      <span>to</span>
      <DateInput value={to} onChange={v => onChange(from, v)} />
      {(from || to) && (
        <button type="button" onClick={() => onChange('', '')}
          className="text-xs underline underline-offset-2 text-grayscale-1100">clear</button>
      )}
    </div>
  </div>
);

export const Check: React.FC<{
  checked: boolean; onChange: (v: boolean) => void; children: React.ReactNode;
}> = ({ checked, onChange, children }) => (
  <label className="flex items-center gap-2 text-sm text-grayscale-1200 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
      className="accent-primary-500" />
    {children}
  </label>
);

/** A short fixed list as checkboxes, inline — every option visible at once. */
export const CheckList: React.FC<{
  label: string; options: string[]; labels?: Record<string, string>;
  values: string[]; onChange: (v: string[]) => void;
}> = ({ label, options, labels, values, onChange }) => (
  <div className="flex flex-col gap-1 text-xs text-grayscale-1100">
    <span>{label}{values.length ? '' : ' · any'}</span>
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      {options.map(o => (
        <Check key={o} checked={values.includes(o)}
          onChange={on => onChange(on ? [...values, o] : values.filter(v => v !== o))}>
          {labels?.[o] ?? o}
        </Check>
      ))}
    </div>
  </div>
);

/**
 * A long list (countries) as a dropdown of checkboxes with a search box.
 * Values that are not in the option list (typed, or left over from an earlier
 * list) stay selected and visible, so nothing narrows the audience unseen.
 */
export const MultiSelect: React.FC<{
  label: string; options: string[]; values: string[]; onChange: (v: string[]) => void;
  placeholder?: string; allowFree?: boolean;
  /** Display text per option value (e.g. "India (1,204)"); the value is what is sent. */
  labels?: Record<string, string>;
}> = ({ label, options, values, onChange, placeholder, allowFree, labels }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const off = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', off);
    return () => document.removeEventListener('mousedown', off);
  }, [open]);
  const all = useMemo(() => Array.from(new Set([...values, ...options])), [values, options]);
  const show = (o: string) => labels?.[o] ?? o;
  const shown = all.filter(o => show(o).toLowerCase().includes(q.trim().toLowerCase()));
  const toggle = (o: string) => onChange(values.includes(o) ? values.filter(v => v !== o) : [...values, o]);
  const summary = values.length === 0 ? (placeholder || 'Any')
    : values.length <= 2 ? values.map(show).join(', ') : `${values.length} selected`;
  return (
    <div className="flex flex-col gap-1 text-xs text-grayscale-1100 relative" ref={box}>
      <span>{label}</span>
      <button type="button" onClick={() => setOpen(o => !o)}
        className={`h-9 px-3 rounded-md border-[0.5px] bg-white text-sm text-left min-w-[12rem] max-w-[18rem] truncate ${
          values.length ? 'border-primary-500 text-grayscale-1300' : 'border-primary-300 text-grayscale-1100'}`}>
        {summary} <span className="float-right">▾</span>
      </button>
      {open && (
        <div className="absolute z-40 top-full mt-1 left-0 w-72 bg-white border border-grayscale-300 rounded-lg shadow-lg p-2 flex flex-col gap-2">
          <input autoFocus value={q} onChange={e => setQ(e.target.value)}
            placeholder={allowFree ? 'Search, or type and press Enter…' : 'Search…'}
            onKeyDown={e => {
              if (e.key === 'Enter' && allowFree && q.trim()) {
                e.preventDefault();
                if (!values.includes(q.trim())) onChange([...values, q.trim()]);
                setQ('');
              }
            }}
            className="h-8 px-2 rounded border-[0.5px] border-primary-300 text-sm" />
          <div className="max-h-60 overflow-y-auto flex flex-col gap-0.5">
            {shown.map(o => (
              <label key={o} className="flex items-center gap-2 text-sm text-grayscale-1200 px-1 py-0.5 rounded hover:bg-grayscale-100 cursor-pointer">
                <input type="checkbox" className="accent-primary-500" checked={values.includes(o)}
                  onChange={() => toggle(o)} />
                <span className="truncate">{show(o)}</span>
              </label>
            ))}
            {!shown.length && <div className="text-xs text-grayscale-1100 px-1 py-2">No match.</div>}
          </div>
          <div className="flex justify-between items-center border-t border-grayscale-200 pt-1.5">
            <span className="text-[11px]">{values.length} selected</span>
            <div className="flex gap-2">
              {values.length > 0 && <Btn small onClick={() => onChange([])}>Clear</Btn>}
              <Btn small onClick={() => setOpen(false)}>Done</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Overlays
// ---------------------------------------------------------------------------
export const Modal: React.FC<{
  title: string; onClose: () => void; children: React.ReactNode; footer?: React.ReactNode;
}> = ({ title, onClose, children, footer }) => (
  <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
    <div role="dialog" aria-modal="true" aria-label={title}
      className="bg-white rounded-xl w-full max-w-lg p-5 flex flex-col gap-3 shadow-xl"
      onClick={e => e.stopPropagation()}>
      <h2 className="text-base font-bold text-grayscale-1300 font-grotesk">{title}</h2>
      <div className="text-sm text-grayscale-1300 flex flex-col gap-2">{children}</div>
      {footer && <div className="flex justify-end gap-2 pt-1">{footer}</div>}
    </div>
  </div>
);

export const Drawer: React.FC<{
  title: string; subtitle?: string; onClose: () => void; children: React.ReactNode;
}> = ({ title, subtitle, onClose, children }) => (
  <div className="fixed inset-0 z-50 bg-black/30 flex justify-end" onClick={onClose}>
    <div className="bg-grayscale-100 w-full max-w-3xl h-full overflow-y-auto p-6 flex flex-col gap-4"
      onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-grayscale-1300 font-grotesk truncate">{title}</h2>
          {subtitle && <div className="text-xs text-grayscale-1100">{subtitle}</div>}
        </div>
        <Btn onClick={onClose}>Close</Btn>
      </div>
      {children}
    </div>
  </div>
);

export const Badge: React.FC<{ tone?: 'blue' | 'amber' | 'red' | 'green' | 'grey'; children: React.ReactNode }> =
  ({ tone = 'blue', children }) => {
  const look = {
    blue: 'bg-primary-100 text-primary-og-600',
    amber: 'bg-amber-100 text-amber-800',
    red: 'bg-red-100 text-red-700',
    green: 'bg-green-100 text-green-800',
    grey: 'bg-grayscale-200 text-grayscale-1200',
  }[tone];
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded whitespace-nowrap ${look}`}>{children}</span>;
};

// ---------------------------------------------------------------------------
// Shapes the backend may send in more than one spelling
// ---------------------------------------------------------------------------
export const toCtas = (v: any): Cta[] => {
  let arr = v;
  if (typeof v === 'string') {
    try { arr = JSON.parse(v); } catch { arr = []; }
  }
  if (!Array.isArray(arr)) return [];
  return arr.map((c: any) => ({ label: String(c?.label ?? c?.text ?? ''), url: String(c?.url ?? c?.href ?? '') }));
};

export type Placeholder = { name: string; meaning: string; sample?: string };

/** Placeholders arrive as ["first_name — meaning"], [{name, meaning}] or
 *  {first_name: "meaning"}; all three come out as [{name, meaning}]. */
export const toPlaceholders = (v: any): Placeholder[] => {
  const clean = (s: any) => String(s ?? '').trim().replace(/^\{+|\}+$/g, '');
  if (!v) return [];
  if (Array.isArray(v)) {
    return v.map((p: any) => {
      if (typeof p === 'string') {
        const m = p.match(/^\s*\{?([A-Za-z_][A-Za-z0-9_]*)\}?\s*(?:[—–:\-=]+\s*(.*))?$/);
        return m ? { name: m[1], meaning: (m[2] || '').trim() } : { name: clean(p), meaning: '' };
      }
      return {
        name: clean(p?.name ?? p?.key ?? p?.placeholder ?? p?.token),
        meaning: String(p?.meaning ?? p?.description ?? p?.desc ?? p?.help ?? ''),
        sample: p?.sample ?? p?.example,
      };
    }).filter(p => p.name);
  }
  if (typeof v === 'object') {
    return Object.entries(v).map(([k, m]) => ({
      name: clean(k),
      meaning: typeof m === 'string' ? m : String((m as any)?.meaning ?? (m as any)?.description ?? ''),
    }));
  }
  return [];
};

const PH_RE = /\{([A-Za-z_][A-Za-z0-9_]*)\}/g;
export const placeholdersIn = (...texts: string[]): string[] => {
  const out = new Set<string>();
  texts.forEach(t => { for (const m of (t || '').matchAll(PH_RE)) out.add(m[1]); });
  return Array.from(out);
};

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------
const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#x27;');

// Same pattern as email_templates._LINK_RE.
const LINK_RE = /(https?:\/\/[^\s<>)\],]+|[\w.+-]+@[\w-]+\.[\w.-]+)/g;
const linkify = (escaped: string) => escaped.replace(LINK_RE, t => {
  const href = t.startsWith('http') ? t : `mailto:${t}`;
  return `<a href="${href}" style="color:#1a73e8;">${t}</a>`;
});

const MISSING_OPEN = '\u0001';
const MISSING_CLOSE = '\u0002';

/** Fill {placeholders} the way the backend's forgiving format_map does, except
 *  that one with no value is kept (marked) rather than blanked — in a draft
 *  preview a blank would hide exactly the thing the editor needs to see. */
const fill = (text: string, values: Record<string, any> | null, mark: boolean) =>
  (text || '').replace(/\{\{/g, '\u0003').replace(/\}\}/g, '\u0004')
    .replace(PH_RE, (m, k) => {
      if (values && values[k] !== undefined && values[k] !== null) return String(values[k]);
      return mark ? `${MISSING_OPEN}${k}${MISSING_CLOSE}` : m;
    })
    .replace(/\u0003/g, '{').replace(/\u0004/g, '}');

const unmarkText = (s: string) => s.replace(/\u0001([^\u0002]*)\u0002/g, '{$1}');
const unmarkHtml = (s: string) => s.replace(/\u0001([^\u0002]*)\u0002/g,
  '<mark style="background:#fde68a;color:#78350f;border-radius:3px;padding:0 2px;">{$1}</mark>');

const SHELL = (inner: string) => `<!DOCTYPE html>
<html>
<body style="margin:0; padding:0; background-color:#ffffff;">
  <div style="font-family: Arial, Helvetica, sans-serif; font-size:15px; line-height:1.5; color:#202124; max-width:640px; margin:0 auto; padding:16px;">
${inner}
  </div>
</body>
</html>`;

/**
 * The unsaved draft as an e-mail, in the browser, so the preview follows every
 * keystroke. It mirrors email_templates.to_html (blank-line paragraphs, a
 * block of "- " / "* " / "• " lines as a list, links made clickable) plus the
 * CTA buttons; the server's render of the SAVED version stays the reference,
 * and the panel says which of the two is on screen.
 */
export const renderDraft = (
  d: { subject: string; body: string; ctas: Cta[] },
  values: Record<string, any> | null,
): { subject: string; html: string; text: string } => {
  const body = fill(d.body, values, true);
  const blocks: string[] = [];
  body.replace(/\r\n/g, '\n').split('\n\n').forEach(raw => {
    const lines = raw.split('\n').map(l => l.replace(/\s+$/, '')).filter(l => l.trim());
    if (!lines.length) return;
    if (lines.every(l => /^\s*([-*]|•)\s/.test(l))) {
      const items = lines.map(l => `<li style="margin-bottom:6px;">${
        linkify(esc(l.replace(/^\s*([-*]|•)\s+/, '')))}</li>`).join('');
      blocks.push(`<ul style="margin-top:0; padding-left:22px;">${items}</ul>`);
    } else {
      blocks.push(`<p>${linkify(esc(lines.join('\n'))).replace(/\n/g, '<br>')}</p>`);
    }
  });
  const ctas = d.ctas.filter(c => c.label.trim() && c.url.trim());
  if (ctas.length) {
    blocks.push(`<p style="margin:20px 0;">${ctas.map(c => {
      const url = fill(c.url, values, false);
      return `<a href="${esc(url)}" style="display:inline-block; background:#1a73e8; color:#ffffff; text-decoration:none; padding:10px 18px; border-radius:6px; font-weight:bold; margin:0 8px 8px 0;">${esc(fill(c.label, values, true))}</a>`;
    }).join('')}</p>`);
  }
  const text = unmarkText(body) + (ctas.length
    ? '\n\n' + ctas.map(c => `${unmarkText(fill(c.label, values, true))}: ${fill(c.url, values, false)}`).join('\n')
    : '');
  return {
    subject: unmarkText(fill(d.subject, values, true)),
    html: unmarkHtml(SHELL(blocks.join('\n'))),
    text,
  };
};

/** Links in a preview open in a new tab instead of inside the frame. */
const withBaseTarget = (html: string) => {
  if (/<head[\s>]/i.test(html)) return html.replace(/<head([^>]*)>/i, '<head$1><base target="_blank">');
  if (/<html[\s>]/i.test(html)) return html.replace(/<html([^>]*)>/i, '<html$1><head><base target="_blank"></head>');
  return `<base target="_blank">${html}`;
};

export const EmailPreview: React.FC<{
  subject: string; html: string; text?: string; from?: string; replyTo?: string; to?: string;
  banner?: React.ReactNode; height?: number;
  /** The mail goes out with no HTML part (send_html false): recipients read
   *  the plain text, so that is what the preview opens on. */
  plainOnly?: boolean;
}> = ({ subject, html, text, from, replyTo, to, banner, height = 560, plainOnly }) => {
  const [mode, setMode] = useState<'html' | 'text'>(plainOnly && text !== undefined ? 'text' : 'html');
  useEffect(() => { setMode(plainOnly && text !== undefined ? 'text' : 'html'); }, [plainOnly]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="flex flex-col gap-2 min-w-0">
      {banner}
      <div className="rounded-lg border border-grayscale-300 bg-white overflow-hidden">
        <div className="px-3 py-2 border-b border-grayscale-200 flex flex-col gap-0.5 text-xs text-grayscale-1100">
          {from && <div><span className="w-16 inline-block">From</span><span className="text-grayscale-1300">{from}</span></div>}
          {replyTo && <div><span className="w-16 inline-block">Reply-To</span><span className="text-grayscale-1300">{replyTo}</span></div>}
          {to && <div><span className="w-16 inline-block">To</span><span className="text-grayscale-1300">{to}</span></div>}
          <div className="flex items-start gap-2">
            <span className="w-16 inline-block shrink-0">Subject</span>
            <span className="text-sm font-bold text-grayscale-1300 break-words">{subject || '(no subject)'}</span>
          </div>
          {text !== undefined && (
            <div className="flex gap-1 pt-1">
              {(['html', 'text'] as const).map(m => (
                <button key={m} type="button" onClick={() => setMode(m)}
                  className={`px-2 py-0.5 rounded-full text-[11px] ${mode === m
                    ? 'bg-primary-500 text-grayscale-100' : 'border border-grayscale-300 text-grayscale-1200'}`}>
                  {m === 'html' ? (plainOnly ? 'HTML (not sent)' : 'HTML') : 'Plain text'}
                </button>
              ))}
              {plainOnly && (
                <span className="text-[11px] text-grayscale-1100 self-center ml-1">
                  Sent as plain text only — add a button to send an HTML version.
                </span>
              )}
            </div>
          )}
        </div>
        {mode === 'html' || text === undefined ? (
          <iframe title="E-mail preview" srcDoc={withBaseTarget(html || '')}
            sandbox="allow-popups allow-popups-to-escape-sandbox"
            className="w-full bg-white block" style={{ height }} />
        ) : (
          <pre className="text-xs text-grayscale-1300 p-3 whitespace-pre-wrap break-words overflow-y-auto"
            style={{ height }}>{text}</pre>
        )}
      </div>
    </div>
  );
};

export const fmtFrom = (name?: string, email?: string) =>
  name && email ? `${name} <${email}>` : (email || name || '');
